/**
 * Social identity providers a patient can sign in with and link from their
 * account. Nile-Core mirrors this registry in Auth::SsoProvider; both sides
 * derive the backend path (`/api/external/<key>_sso`) from the same key, so
 * adding a provider is one entry here plus a logo in SsoProviderIcon. Every
 * provider ships behind the one `google-sso` flag.
 */
export type SsoProviderKey = 'google' | 'apple'

export interface SsoProvider {
    key: SsoProviderKey
    label: string
    /** Name of the Cognito identity provider passed as `identity_provider` to the hosted UI. */
    cognitoIdentityProvider: string
}

export const SSO_PROVIDERS: Record<SsoProviderKey, SsoProvider> = {
    google: { key: 'google', label: 'Google', cognitoIdentityProvider: 'Google' },
    apple: { key: 'apple', label: 'Apple', cognitoIdentityProvider: 'SignInWithApple' },
}

export const SSO_PROVIDER_KEYS = Object.keys(SSO_PROVIDERS) as SsoProviderKey[]

export function isSsoProviderKey(value: string | null | undefined): value is SsoProviderKey {
    return Boolean(value) && Object.prototype.hasOwnProperty.call(SSO_PROVIDERS, value as string)
}

export const SSO_FEATURE_FLAG = 'google-sso'

export type SsoLoginErrorKind = 'state' | 'token' | 'config' | 'account_not_found'

/** Value of the `error` query param the login routes redirect to, e.g. `google_sso_token`. */
export const ssoLoginErrorCode = (provider: SsoProviderKey, kind: SsoLoginErrorKind) => `${provider}_sso_${kind}`

const LOGIN_ERROR_MESSAGES: Record<Exclude<SsoLoginErrorKind, 'account_not_found'>, (label: string) => string> = {
    state: (label) => `${label} sign-in could not be verified. Please try again.`,
    token: (label) => `${label} sign-in failed while completing login. Please try again.`,
    config: (label) => `${label} sign-in is temporarily unavailable. Please use your phone number.`,
}

/** Resolves a login `error` query value to its provider and user-facing message. */
export function resolveSsoLoginError(
    errorCode: string | null,
): { provider: SsoProvider; message: string } | null {
    for (const provider of Object.values(SSO_PROVIDERS)) {
        for (const kind of Object.keys(LOGIN_ERROR_MESSAGES) as (keyof typeof LOGIN_ERROR_MESSAGES)[]) {
            if (errorCode === ssoLoginErrorCode(provider.key, kind)) {
                return { provider, message: LOGIN_ERROR_MESSAGES[kind](provider.label) }
            }
        }
    }
    return null
}

/** Resolves a login `error` query value to the provider whose sign-in found no linked account. */
export function resolveSsoAccountNotFoundProvider(errorCode: string | null): SsoProvider | null {
    return (
        Object.values(SSO_PROVIDERS).find(
            (provider) => errorCode === ssoLoginErrorCode(provider.key, 'account_not_found'),
        ) ?? null
    )
}

/**
 * Query params Nile-Core redirects back to the account page with after linking:
 * `<key>_linked=1` or `<key>_link_error=<code>`.
 */
export const ssoLinkedParam = (provider: SsoProviderKey) => `${provider}_linked`

export const ssoLinkErrorParam = (provider: SsoProviderKey) => `${provider}_link_error`

/** Error code the backend uses when the provider account is linked to a different patient. */
export const SSO_ALREADY_LINKED_ERROR = 'already_linked'

const LINK_ERROR_MESSAGES: Record<string, (label: string) => string> = {
    cancelled: (label) => `${label} sign-in was cancelled. You can try again when ready.`,
    auth_state: (label) => `${label} linking could not be verified. Please try again.`,
    token: (label) => `We could not finish ${label} linking. Please try again.`,
    unverified_email: (label) =>
        `Your ${label} account email is not verified. Please verify it with ${label} and try again.`,
    config: (label) => `${label} linking is temporarily unavailable. Please try again later.`,
    link: (label) => `Could not connect ${label} to your account. Please try again.`,
    unavailable: (label) => `${label} linking is not available right now.`,
}

export function ssoLinkErrorMessage(provider: SsoProvider, errorCode: string): string {
    const buildMessage = LINK_ERROR_MESSAGES[errorCode] ?? LINK_ERROR_MESSAGES.link
    return buildMessage(provider.label)
}
